import Compressor from 'compressorjs';
import {catchError, from, of} from 'rxjs';

import {DestroyRef, Directive, ElementRef, HostListener, inject, input, OnInit, output, Renderer2} from '@angular/core';
import {CompanySettingsStore} from '@company-settings/data-access';
import {MainInjector} from '@shared-types';

@Directive({
	selector: '[fileUpload]',
	exportAs: 'fileUpload',
	standalone: true,
})
export class FileUploadDirective implements OnInit {
	readonly fileTypes = input<string[]>([]);

	readonly maxFiles = input(Infinity);
	readonly uploadButton = input<ElementRef>(undefined);
	readonly openCamera = input(false);
	readonly autoOpen = input(false);
	readonly compressionOptions = input<Compressor.Options>({quality: 0.6});

	readonly fileUploaded = output<FileUploadedStruct>();

	/**
	 * emit when file system dialog is opened
	 */
	readonly fileSystemDialogOpened = output<void>();

	private _imageCompression = 5;
	private destroyRef = inject(DestroyRef);
	private destroyed = false;
	private inputElement: HTMLInputElement = null;

	constructor(
		private renderer: Renderer2,
		private el?: ElementRef,
		protected readonly companySettingsStore: CompanySettingsStore = MainInjector.get(CompanySettingsStore),
	) {
		this.destroyRef.onDestroy(() => {
			this.destroyed = true;
			if (this.inputElement) {
				this.renderer.removeChild(document.body, this.inputElement);
			}
		});
	}

	ngOnInit(): void {
		this._imageCompression = this.companySettingsStore.state()?.settings?.imageCompression;
		if (this.autoOpen()) {
			this.onClick();
		}
		this.initializeFileInput();
	}

	@HostListener('click') onClick() {
		if (this.openCamera()) {
			this.renderer.setAttribute(this.inputElement, 'capture', 'camera');
		} else {
			this.renderer.removeAttribute(this.inputElement, 'capture');
		}
		this.renderer.setAttribute(this.inputElement, 'multiple', this.maxFiles() > 1 ? 'true' : 'false');
		this.renderer.setAttribute(this.inputElement, 'accept', this.fileTypes().length > 0 ? this.fileTypes().join(',') : '*/*');

		this.fileSystemDialogOpened.emit();
		this.inputElement.click();
	}

	@HostListener('dragover', ['$event']) onDragOver(event) {
		event.preventDefault();
		event.stopPropagation();
		if (this.uploadButton()) this.renderer.addClass(this.el.nativeElement, 'drag-over');
	}

	@HostListener('dragleave', ['$event']) onDragLeave(event) {
		event.preventDefault();
		event.stopPropagation();
		if (this.uploadButton()) this.renderer.removeClass(this.el.nativeElement, 'drag-over');
	}

	@HostListener('drop', ['$event']) onDrop(event) {
		event.preventDefault();
		event.stopPropagation();
		if (this.uploadButton()) this.renderer.removeClass(this.el.nativeElement, 'drag-over');

		const files: FileList = event.dataTransfer.files;
		if (files.length === 0) {
			return;
		}

		for (let i = 0; i < files.length; i++) {
			const file: File = files[i];
			if (this.isValidFileType(file)) {
				this.uploadFile(file);
			}
		}
	}

	private initializeFileInput() {
		this.inputElement = this.renderer.createElement('input');
		this.renderer.setAttribute(this.inputElement, 'type', 'file');
		this.renderer.setStyle(this.inputElement, 'display', 'none');
		this.renderer.appendChild(document.body, this.inputElement);

		this.renderer.listen(this.inputElement, 'change', e => {
			const files: FileList = this.inputElement.files;
			if (files && files.length > 0) {
				for (let i = 0; i < files.length; i++) {
					const file: File = files[i];
					if (this.isValidFileType(file)) {
						this.uploadFile(file);
					}
				}
			}
			// Reset the input after processing to allow the same file to be selected again
			this.inputElement.value = '';
		});
	}

	private isValidFileType(file: File): boolean {
		const fileType = file.type;
		const normalizedFileType = fileType ? fileType.split('/')[0] : '';
		const normalizedAllowedTypes = this.fileTypes().map(type => type.split('/')[0]);
		if (normalizedAllowedTypes.includes(normalizedFileType)) return true;

		const fileExtension = file.name.split('.').pop();
		const fileTypes = this.fileTypes();
		return fileTypes.length === 0 || fileTypes.includes(file.type) || fileTypes.includes(fileExtension);
	}

	private compressImage(file: File, maxWidth: number, maxHeight: number): Promise<File> {
		return new Promise((resolve, reject) => {
			new Compressor(file, {
				...this.compressionOptions(),
				maxWidth,
				maxHeight,
				success(result) {
					resolve(result as File);
				},
				error(err) {
					reject(err);
				},
			});
		});
	}

	private calculateDimensionsBasedOnCompression(
		compressionLevel: number,
		originalWidth: number,
		originalHeight: number,
	): {
		maxWidth: number;
		maxHeight: number;
	} {
		const maxScale = 0.1; // 10% for compression level 10 (highest compression)
		const minScale = 1; // 100% for compression level 0 (no compression)

		// Adjust scale factor calculation to invert the logic
		const scaleFactor = minScale - ((minScale - maxScale) * compressionLevel) / 10;

		// Apply scale factor to original dimensions
		const adjustedWidth = originalWidth * scaleFactor;
		let adjustedHeight = originalHeight * scaleFactor;

		// Maintain aspect ratio (in case of non-uniform scaling, if needed)
		const aspectRatio = originalWidth / originalHeight;
		if (adjustedWidth / adjustedHeight !== aspectRatio) {
			adjustedHeight = adjustedWidth / aspectRatio;
		}

		return {
			maxWidth: Math.round(adjustedWidth),
			maxHeight: Math.round(adjustedHeight),
		};
	}

	private uploadFile(file: File) {
		if (this.compressionOptions() && file.type.includes('image')) {
			const url = URL.createObjectURL(file);
			const img = new Image();
			img.onload = () => {
				const originalWidth = img.width;
				const originalHeight = img.height;
				const {maxWidth, maxHeight} = this.calculateDimensionsBasedOnCompression(this._imageCompression, originalWidth, originalHeight);

				from(this.compressImage(file, maxWidth, maxHeight))
					.pipe(
						catchError(err => {
							console.error(err);
							return of(null);
						}),
					)
					.subscribe(compressedFile => {
						if (compressedFile && !this.destroyed) {
							this.readFileAndEmit(compressedFile);
						}
						URL.revokeObjectURL(url); // Clean up
					});
			};
			img.onerror = () => {
				console.error('Failed to load image for dimension analysis.');
				URL.revokeObjectURL(url);
			};
			img.src = url;
		} else {
			this.readFileAndEmit(file);
		}
	}

	private readFileAndEmit(file: File) {
		if (this.destroyed) return;

		const reader = new FileReader();
		reader.readAsDataURL(file);

		reader.onloadstart = () => {
			// Show loading indicator
		};

		reader.onload = () => {
			// Check if component is still alive before emitting
			if (!this.destroyed) {
				this.fileUploaded.emit({
					file: reader.result as string,
					fileName: file.name,
					fileType: file.type,
					fileSize: this.formatFileSize(file.size),
				});
			}
		};

		reader.onerror = error => {
			if (!this.destroyed) {
				console.error('field-uploader.directive:', error);
			}
		};
	}

	private formatFileSize(bytes: number): string {
		if (bytes === 0) return '0 Byte';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		const fileSize = (bytes / Math.pow(k, i)).toFixed(1);
		return fileSize + ' ' + sizes[i];
	}
}

export type FileUploadedStruct = {
	file: string;
	fileName: string;
	fileType?: string;
	fileSize?: string;
};
